import { useMutation, useQuery } from '@apollo/client';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { SvbrgLanguage, SvbrgQuoteComplianceCheck } from '@/generated/gql/graphql';
import { useChatSession } from '~/modules';

import { INTAKE_QUOTE_COMPLIANCE_MUTATION } from './IntakeQuoteComplianceStep.mutation.gql';
import { INTAKE_QUOTE_COMPLIANCE_QUERY } from './IntakeQuoteComplianceStep.query.gql';

export interface UseQuoteComplianceStepReturn {
  complianceData?: Union.Nullable<SvbrgQuoteComplianceCheck>;
  loading: boolean;
}

export const useQuoteComplianceStep = (): UseQuoteComplianceStepReturn => {
  const session = useChatSession();
  const { onError } = useCustomSnackbar();
  const { i18n } = useTranslation();

  const { data: queriedData, loading: queryLoading } = useQuery(INTAKE_QUOTE_COMPLIANCE_QUERY, {
    variables: session,
    fetchPolicy: 'cache-and-network',
  });

  const [runCompliance, { loading: mutationLoading, called: complianceCalled }] = useMutation(
    INTAKE_QUOTE_COMPLIANCE_MUTATION,
    {
      refetchQueries: [{ query: INTAKE_QUOTE_COMPLIANCE_QUERY, variables: session }],
    },
  );

  const loading = queryLoading || mutationLoading;

  // Get the language from user settings
  const language = i18n.language === 'dx' ? SvbrgLanguage.DE : (i18n.language.toUpperCase() as SvbrgLanguage);

  // Extract compliance data from the query
  const complianceData = queriedData?.sevenbridges?.chatSession?.quoteCompliance;

  // Auto-trigger compliance check if not already done
  useEffect(() => {
    async function checkCompliance() {
      try {
        await runCompliance({
          variables: {
            data: {
              chatSessionId: session.id,
              language,
            },
          },
        });
      } catch (error) {
        onError(error);
      }
    }

    const existingCompliance = queriedData?.sevenbridges?.chatSession?.quoteCompliance;

    if (!existingCompliance && !loading && !complianceCalled) {
      checkCompliance();
    }
  }, [queriedData, loading, complianceCalled, runCompliance, session.id, language, onError]);

  return {
    complianceData,
    loading,
  };
};
